import {
  AdjustmentsHorizontalIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { getLoanInputFields } from 'config/loan.input.fields.constants'
import { IVisibleLogic } from 'config/loan.input.visibility.type'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  deleteReviewFormTemplate,
  getReviewFormTemplates,
  setReviewFormCategories,
  toggleReviewFormTemplateVisible,
  updateReviewFormTemplateOrder,
  updateReviewFormTemplateVisibleLogic,
} from 'services'
import { Select } from 'stories/components'
import { confirm } from 'utils'
import { promptVisibleLogic } from 'utils/modals/promptVisibleLogic'

import type { ReviewFormCategory } from './ReviewFormItems'

export interface ReviewFormTemplate {
  id: number
  no: number
  categoryId: string
  title: string
  description: string
  order: number
  visible: boolean
  visibleLogic: IVisibleLogic[][]
}

export const ReviewFormTemplates = ({
  isLoading,
  setLoading,
  categories,
  setCategories,
  onEdit,
  values,
  setValues,
}: {
  isLoading: boolean
  setLoading: Function
  categories: ReviewFormCategory[]
  setCategories: Function
  onEdit: (v: ReviewFormTemplate) => void
  values: ReviewFormTemplate[]
  setValues: Function
}) => {
  const { loanFieldProperties } = useSelector((state: any) => ({
    loanFieldProperties: state.globalConfigReducer[GlobalConfigType.LoanFields],
  }))

  const [categoryFilter, setCategoryFilter] = useState<string>()

  useEffect(() => {
    setLoading(true)
    getReviewFormTemplates().then((values: ReviewFormTemplate[]) => {
      setValues(values)
      setLoading(false)
    })
  }, [])

  const categoryMap = useMemo(() => {
    const categoryMap: Record<string, string> = {}
    categories.forEach((category) => {
      if (!category.value) return
      categoryMap[category.id] = category.value
    })
    return categoryMap
  }, [categories])

  const onChangeOrder = async (val: string, value: ReviewFormTemplate) => {
    setLoading(true)
    const newOrder = Number(val)
    await updateReviewFormTemplateOrder(value.id, newOrder)
    const updatedValues = await getReviewFormTemplates()
    setValues(updatedValues)
    setLoading(false)
  }

  const onToggleVisible = async (item: ReviewFormTemplate) => {
    setLoading(true)
    await toggleReviewFormTemplateVisible(item.id)
    const updatedValues = await getReviewFormTemplates()
    setValues(updatedValues)
    setLoading(false)
  }

  const onEditCategoryVisibleLogic = async (category: ReviewFormCategory) => {
    const result = (await promptVisibleLogic(`Visible Conditions - ${category.value || ''}`, {
      loanFieldProperties,
      fieldInputs: getLoanInputFields(),
      value: category.visibleLogic || [],
      noLogicMessage: 'No conditions set. This category will be visible for all loans.',
      hasNotVisible: true,
    })) as IVisibleLogic[][] | false
    if (result === false) return

    const newCategories = cloneDeep(categories).map((c) =>
      c.id === category.id ? { ...c, visibleLogic: result as IVisibleLogic[][] } : c,
    )
    setCategories(newCategories)
    setLoading(true)
    await setReviewFormCategories(newCategories.filter((c) => c.value !== null))
    setLoading(false)
  }

  const onDelete = async (item: ReviewFormTemplate) => {
    const result = await confirm('Do you want to remove this template?')
    if (!result) return

    const index = values.findIndex((v) => v.no == item.no)
    if (index === -1) return
    await deleteReviewFormTemplate(values[index].id)

    const newValues = cloneDeep(values)
    newValues.splice(index, 1)
    setValues(newValues)
  }

  const onEditVisibleLogic = async (item: ReviewFormTemplate) => {
    const result = (await promptVisibleLogic(`Visible Conditions - ${item.title}`, {
      loanFieldProperties,
      fieldInputs: getLoanInputFields(),
      value: item.visibleLogic || [],
      noLogicMessage: 'No conditions set. This template will be visible for all loans.',
      hasNotVisible: true,
    })) as IVisibleLogic[][] | false
    if (result === false) return

    setLoading(true)
    await updateReviewFormTemplateVisibleLogic(item.id, result as any)
    const updatedValues = await getReviewFormTemplates()
    setValues(updatedValues)
    setLoading(false)
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between">
        <div className="flex items-center flex-wrap flex-auto">
          <div className="w-72">
            <Select
              id="searchBy-category"
              title="Filter by Category"
              hasDefaultOption
              defaultOptionText="All"
              options={categoryMap}
              value={categoryFilter}
              onChange={(v) => setCategoryFilter(v)}
            />
          </div>
        </div>
      </div>
      <div className="relative overflow-auto shadow-md sm:rounded-lg">
        <LayoutLoading show={isLoading} />
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3 w-[40px]">
                No
              </th>
              <th scope="col" className="px-3 py-3 w-[120px]">
                Category
              </th>
              <th scope="col" className="px-3 py-3">
                Title
              </th>
              <th scope="col" className="px-3 py-3 w-[50px]">
                Order
              </th>
              <th scope="col" className="px-3 py-3 w-[150px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {categories
              .filter((v) => (categoryFilter ? v.id == categoryFilter : true))
              .map((category, index) => {
                const categoryHasVisibleLogic = !!category.visibleLogic && category.visibleLogic.length > 0
                return [
                  <tr
                    className="border-b bg-gray-100 dark:bg-gray-700 dark:border-gray-600"
                    key={`header-${category.id}`}
                  >
                    <td
                      className="px-3 py-2 font-semibold text-gray-800 dark:text-white uppercase text-xs tracking-wide"
                      colSpan={4}
                    >
                      {index + 1}. {category.value}
                    </td>
                    <td className="px-3 py-2">
                      <span className="flex gap-2">
                        <span
                          className={`p-1 hover-shadow1 cursor-pointer rounded ${
                            categoryHasVisibleLogic ? 'text-shade-blue' : 'text-gray-400'
                          }`}
                          onClick={() => onEditCategoryVisibleLogic(category)}
                          title={
                            categoryHasVisibleLogic
                              ? 'Has visible conditions'
                              : 'Set visible conditions'
                          }
                        >
                          <AdjustmentsHorizontalIcon className="w-4 h-4" />
                        </span>
                      </span>
                    </td>
                  </tr>,
                  values
                    .filter((item) => item.categoryId == category.id)
                    .sort((a, b) =>
                      a.order !== b.order ? (a.order > b.order ? 1 : -1) : a.title.localeCompare(b.title),
                    )
                    .map((value, index) => {
                      const totalCount = values.filter((item) => item.categoryId == category.id).length
                      const orderOptions = Array.apply(null, Array(totalCount)).map((_, index) => index.toString())
                      const hasVisibleLogic = value.visibleLogic && value.visibleLogic.length > 0
                      const itemDimmed = value.visible === false
                      return (
                        <tr
                          className={`${
                            index % 2 ? 'bg-gray-50' : 'bg-white'
                          } border-b dark:bg-gray-800 dark:border-gray-700 ${itemDimmed ? 'opacity-50' : ''}`}
                          key={value.no}
                        >
                          <td
                            scope="row"
                            className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap"
                          >
                            {value.no}
                          </td>

                          <td
                            scope="row"
                            className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap"
                          >
                            {categoryMap[value.categoryId]}
                          </td>

                          <td
                            scope="row"
                            className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap"
                          >
                            {value.title}
                          </td>
                          <td>
                            <select
                              onChange={(e) => onChangeOrder(e.target.value, value)}
                              value={index}
                              className="block rounded py-1.5 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                            >
                              {orderOptions.map((val) => (
                                <option key={val}>{val}</option>
                              ))}
                            </select>
                          </td>

                          <td className="px-3 py-2">
                            <span className="flex gap-2">
                              <span
                                className="text-shade-blue p-1 hover-shadow1 cursor-pointer rounded"
                                onClick={() => onEdit(value)}
                              >
                                <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                              </span>
                              <span
                                className={`p-1 hover-shadow1 cursor-pointer rounded ${
                                  hasVisibleLogic ? 'text-shade-blue' : 'text-gray-400'
                                }`}
                                onClick={() => onEditVisibleLogic(value)}
                                title={hasVisibleLogic ? 'Has visible conditions' : 'Set visible conditions'}
                              >
                                <AdjustmentsHorizontalIcon className="w-4 h-4" />
                              </span>
                              <span
                                className={`p-1 hover-shadow1 cursor-pointer rounded ${
                                  value.visible !== false ? 'text-shade-blue' : 'text-gray-400'
                                }`}
                                onClick={() => onToggleVisible(value)}
                                title={value.visible !== false ? 'Visible - click to hide' : 'Hidden - click to show'}
                              >
                                {value.visible !== false ? (
                                  <EyeIcon className="w-4 h-4" />
                                ) : (
                                  <EyeSlashIcon className="w-4 h-4" />
                                )}
                              </span>
                              <span
                                className="text-red-800 p-1 hover-shadow1 cursor-pointer rounded"
                                onClick={() => onDelete(value)}
                              >
                                <TrashIcon className="w-4 h-4"></TrashIcon>
                              </span>
                            </span>
                          </td>
                        </tr>
                      )
                    }),
                ]
              })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
